/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import useSWRImmutable from "swr";

import { useAccountContext } from "../account/account.hooks";
import { useApiConfig } from "../api-config";

import { AuthRes, auth } from "./auth.fetch";

export const useAccountLoginRequired = (): { accountLoading: boolean; isLoggedIn?: boolean } => {
	const { account, isLoading } = useAccountContext();
	const router = useRouter();
	const location = useLocation();
	const locale = useLocale();
	const isLoggedIn = account.isLoggedIn;
	if (account && !isLoading && !isLoggedIn) {
		console.info("useAccountLoginRequired() redirecting to /auth");
		router.push(`/${locale}/auth?redirectUrl=${location.toString()}`);
	}
	return { accountLoading: isLoading, isLoggedIn };
};

export const useAuth = (
	disable?: boolean,
): {
	auth?: AuthRes;
	isLoading: boolean;
	error?: string;
} => {
	const { refreshAccount } = useAccountContext();
	const { serverUrl } = useApiConfig();
	const { data, error } = useSWRImmutable(!disable ? ["auth", serverUrl] : null, (_key, url) => auth(url), {
		errorRetryCount: 1,
	});

	// Trigger account refresh when tokens are upgraded. Guard on `data` so
	// this only fires after a successful auth fetch (not on initial mount,
	// and not at all when `disable` is true — in that case SWR is paused
	// and `data` stays undefined).
	useEffect(() => {
		if (!data) return;
		console.info("Token upgraded, refreshing account data");
		void refreshAccount();
	}, [data, refreshAccount]);

	return {
		auth: data,
		isLoading: !data && !error,
		error,
	};
};
